/**
 * CRDT Metrics - Performance Tracking for CRDT Operations
 *
 * Tracks timing and count metrics for Store rebuilds and batch operations.
 * Used for debugging and performance monitoring.
 */

// ============================================
// Performance Metrics State
// ============================================
const perfMetrics = {
  storeRebuilds: 0,
  totalRebuildTimeMs: 0,
  maxRebuildTimeMs: 0,
  lastRebuildTimeMs: 0,
  batchOperations: 0,
  totalBatchTimeMs: 0,
  // Rolling window for recent rebuilds (last 10)
  recentRebuilds: [],
};

/**
 * Record a Store rebuild timing
 * @param {number} elapsedMs - Time taken in milliseconds
 */
export function recordRebuildMetric(elapsedMs) {
  perfMetrics.storeRebuilds++;
  perfMetrics.totalRebuildTimeMs += elapsedMs;
  perfMetrics.lastRebuildTimeMs = elapsedMs;
  if (elapsedMs > perfMetrics.maxRebuildTimeMs) {
    perfMetrics.maxRebuildTimeMs = elapsedMs;
  }
  // Keep last 10 rebuilds for averaging
  perfMetrics.recentRebuilds.push(elapsedMs);
  if (perfMetrics.recentRebuilds.length > 10) {
    perfMetrics.recentRebuilds.shift();
  }
}

/**
 * Record a batch operation timing
 * @param {number} elapsedMs - Time taken in milliseconds
 */
export function recordBatchMetric(elapsedMs) {
  perfMetrics.batchOperations++;
  perfMetrics.totalBatchTimeMs += elapsedMs;
}

/**
 * Get current performance metrics (for debugging)
 * @returns {{
 *   storeRebuilds: number,
 *   avgRebuildTimeMs: string,
 *   maxRebuildTimeMs: string,
 *   lastRebuildTimeMs: string,
 *   recentAvgRebuildTimeMs: string,
 *   batchOperations: number,
 *   avgBatchTimeMs: string
 * }} Performance metrics snapshot
 */
export function getPerformanceMetrics() {
  const avgRebuild = perfMetrics.storeRebuilds > 0
    ? perfMetrics.totalRebuildTimeMs / perfMetrics.storeRebuilds
    : 0;
  const recentAvg = perfMetrics.recentRebuilds.length > 0
    ? perfMetrics.recentRebuilds.reduce((a, b) => a + b, 0) / perfMetrics.recentRebuilds.length
    : 0;

  return {
    storeRebuilds: perfMetrics.storeRebuilds,
    avgRebuildTimeMs: avgRebuild.toFixed(2),
    maxRebuildTimeMs: perfMetrics.maxRebuildTimeMs.toFixed(2),
    lastRebuildTimeMs: perfMetrics.lastRebuildTimeMs.toFixed(2),
    recentAvgRebuildTimeMs: recentAvg.toFixed(2),
    batchOperations: perfMetrics.batchOperations,
    avgBatchTimeMs: perfMetrics.batchOperations > 0
      ? (perfMetrics.totalBatchTimeMs / perfMetrics.batchOperations).toFixed(2)
      : "0.00",
  };
}

/**
 * Reset performance metrics (for testing or document switch)
 */
export function resetPerformanceMetrics() {
  perfMetrics.storeRebuilds = 0;
  perfMetrics.totalRebuildTimeMs = 0;
  perfMetrics.maxRebuildTimeMs = 0;
  perfMetrics.lastRebuildTimeMs = 0;
  perfMetrics.batchOperations = 0;
  perfMetrics.totalBatchTimeMs = 0;
  perfMetrics.recentRebuilds = [];
}
